import type React from 'react';
import type { CSSProperties } from 'react';

import { cn } from '@change/ds-utils';

// [x, y, size, delay in seconds, strong color] — the canvas's six sparks
const SPARKS: ReadonlyArray<readonly [number, number, number, number, boolean]> = [
	[96, 14, 11, 0, true],
	[104, 42, 7, 0.1, false],
	[72, 10, 6, 0.18, false],
	[114, 22, 5, 0.26, true],
	[86, 50, 4.5, 0.32, true],
	[60, 24, 4, 0.22, true],
];

// the glow and each spark scale and rotate around their own center
const SCALE_FROM_CENTER: CSSProperties = { transformBox: 'fill-box', transformOrigin: 'center' };

const starPath = (size: number) => `M0 ${-size} Q0 0 ${size} 0 Q0 0 0 ${size} Q0 0 ${-size} 0 Q0 0 0 ${-size} Z`;

/**
 * The hero's decorative wand: it rocks while sparks pop around its tip and a glow pulses behind them.
 * Under reduced motion every animation is dropped and the un-animated markup is the resting pose.
 */
export const AnimatedWand: React.FC<EmptyProps> = () => {
	return (
		<svg
			width="128"
			height="96"
			viewBox="0 0 128 96"
			fill="none"
			aria-hidden="true"
			focusable="false"
			className="overflow-visible"
			data-qa="vision-hero-wand"
		>
			<circle
				cx={82}
				cy={30}
				r={10}
				fill="currentColor"
				className="text-fill-selected-weaker animate-wand-glow motion-reduce:animate-none"
				style={SCALE_FROM_CENTER}
				data-qa="vision-hero-wand-glow"
			/>
			{SPARKS.map(([x, y, size, delay, strong]) => (
				<g key={`${x}-${y}`} transform={`translate(${x} ${y})`}>
					<path
						d={starPath(size)}
						fill="currentColor"
						className={cn(
							'animate-wand-spark motion-reduce:animate-none',
							strong ? 'text-icon-information' : 'text-fill-selected-weaker',
						)}
						style={{ ...SCALE_FROM_CENTER, animationDelay: `${delay}s` }}
						data-qa="vision-hero-wand-spark"
					/>
				</g>
			))}
			<g
				className="animate-wand-wave motion-reduce:animate-none"
				// the wand pivots on its handle end
				style={{ transformBox: 'view-box', transformOrigin: '30px 82px' }}
				data-qa="vision-hero-wand-stick"
			>
				<line
					x1={30}
					y1={82}
					x2={78}
					y2={34}
					stroke="currentColor"
					strokeWidth={7}
					strokeLinecap="round"
					className="text-icon-information"
				/>
				<line
					x1={72}
					y1={40}
					x2={79}
					y2={33}
					stroke="currentColor"
					strokeWidth={7}
					strokeLinecap="round"
					className="text-fill-selected-weaker"
				/>
			</g>
		</svg>
	);
};
